/**
 * Pure decisions of the error notification dialog (`PopupAndNotifService.showNotification(…, 'error')`).
 *
 * Error notifications used to be ngx-toastr toasts, which disappear on their own and were called
 * with the arguments in both orders: most pages pass a bare `'Error'` / `'Error!'` as the message
 * and the real sentence as the title (the toast showed both, so nobody noticed). A dialog shows a
 * title and a body, so the order matters: the sentence that explains the failure must be the body.
 */

export interface ErrorNotificationContent {
  title: string;
  message: string;
  /** Buttons that take the user where an action is done; the dialog closes with the clicked `id`. */
  links?: ErrorNotificationLink[];
}

export interface ErrorNotificationLink {
  id: string;
  label: string;
}

/** A paragraph of the dialog body, or a list of actions (lines the backend started with a bullet). */
export interface ErrorNotificationBlock {
  type: 'paragraph' | 'list';
  lines: string[];
}

export const ERROR_NOTIFICATION_DIALOG_WIDTH = '480px';
export const ERROR_NOTIFICATION_DIALOG_WIDE = '760px';

/** Lines that start with a bullet ("• …", "- …") are list items: the backend writes one per action. */
const LIST_LINE = /^\s*(?:•|-|\*|•)\s+(.*)$/;

/**
 * Splits a message into paragraphs and lists, so a message that enumerates what to do ("To unblock
 * it: • Validate a bank account… • Fill in the Euribor…") is rendered as a real list instead of a
 * wall of text. Text only: the lines are interpolated, never bound as HTML.
 */
export function errorNotificationBlocks(message: string | null | undefined): ErrorNotificationBlock[] {
  const blocks: ErrorNotificationBlock[] = [];
  for (const raw of String(message ?? '').split(/\r?\n/)) {
    const line = raw.trim();
    if (line === '') {
      continue;
    }
    const item = LIST_LINE.exec(line);
    const type: ErrorNotificationBlock['type'] = item ? 'list' : 'paragraph';
    const text = item ? item[1].trim() : line;
    const last = blocks[blocks.length - 1];
    if (last && last.type === 'list' && type === 'list') {
      last.lines.push(text);
    } else {
      blocks.push({ type, lines: [text] });
    }
  }
  return blocks;
}

/**
 * Width of the dialog: wide when the message is a list of actions or a long explanation, so the
 * user does not scroll a narrow column to read which lenders or periods are concerned.
 */
export function errorNotificationDialogWidth(message: string | null | undefined): string {
  const blocks = errorNotificationBlocks(message);
  const hasList = blocks.some((b) => b.type === 'list');
  const length = blocks.reduce((n, b) => n + b.lines.join(' ').length, 0);
  return hasList || length > 320 ? ERROR_NOTIFICATION_DIALOG_WIDE : ERROR_NOTIFICATION_DIALOG_WIDTH;
}

export const ERROR_NOTIFICATION_DEFAULT_TITLE = 'Action not completed';

export const ERROR_NOTIFICATION_DEFAULT_MESSAGE =
  'The operation could not be completed. Please try again, and contact IT support if the problem persists.';

/** Words that name the fact that something failed without saying what or why. */
const GENERIC_ERROR_TEXT = /^(an?\s+)?(error|errors|erreur|failed|failure|ko|oops|warning|alert|unknown error|error occurred|error occured)\s*[!.:]*$/i;

/** Plain text of whatever a page passed in: a string, an Error, an HTTP error body, or nothing. */
export function errorNotificationText(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  const nested = value?.error?.message ?? value?.message;
  return typeof nested === 'string' ? nested.trim() : '';
}

export function isGenericErrorText(text: string | null | undefined): boolean {
  const value = (text ?? '').trim();
  return value === '' || GENERIC_ERROR_TEXT.test(value);
}

/**
 * Title and body of the error dialog. The explanatory sentence always ends up in the body, whatever
 * the order the caller used; a generic word is never shown as the only explanation.
 */
export function resolveErrorNotificationContent(message: any, title: any): ErrorNotificationContent {
  let body = errorNotificationText(message);
  let heading = errorNotificationText(title);

  if (isGenericErrorText(body) && !isGenericErrorText(heading)) {
    [body, heading] = [heading, ''];
  }
  if (isGenericErrorText(body)) {
    body = ERROR_NOTIFICATION_DEFAULT_MESSAGE;
  }
  if (isGenericErrorText(heading) || heading === body) {
    heading = ERROR_NOTIFICATION_DEFAULT_TITLE;
  }

  return { title: heading, message: body };
}
